import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import type { AdminPricingPack, Pool } from '../api/types';
import { formatMinutes, formatMoney, POOL_LABEL } from '../lib/format';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

const POOLS: Pool[] = ['general', 'simulator'];

// Fields stay strings until submit so a half-typed "150." is not mangled.
const cleanDigits = (value: string) => value.replace(/[^0-9]/g, '');
const cleanPrice = (value: string) => value.replace(/[^0-9.]/g, '');
const parseWhole = (value: string, min: number) => {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isInteger(parsed) && parsed >= min ? parsed : null;
};
const parsePrice = (value: string) => {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const formatValidity = (days: number) => (days === 1 ? '1 day' : `${days} days`);

// Price per hour is how the owner compares packs against each other — a bigger
// pack should work out cheaper per hour, and a typo usually shows up here.
const perHour = (pack: Pick<AdminPricingPack, 'price' | 'minutes'>) =>
  (Number(pack.price) / pack.minutes) * 60;

function PackFormDialog({
  pack,
  defaultPool,
  onClose,
  onSaved,
}: {
  pack: AdminPricingPack | null;
  defaultPool: Pool;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [pool, setPool] = useState<Pool>(pack?.pool ?? defaultPool);
  const [label, setLabel] = useState(pack?.label ?? '');
  const [minutes, setMinutes] = useState(pack ? String(pack.minutes) : '');
  const [price, setPrice] = useState(pack ? String(Number(pack.price)) : '');
  const [validity, setValidity] = useState(pack ? String(pack.validity_days) : '');
  const [order, setOrder] = useState(pack ? String(pack.display_order) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const minutesValue = parseWhole(minutes, 1);
  const priceValue = parsePrice(price);
  const validityValue = parseWhole(validity, 1);
  const orderValue = order.trim() === '' ? undefined : parseWhole(order, 0);
  const valid =
    label.trim().length > 0 &&
    minutesValue !== null &&
    priceValue !== null &&
    validityValue !== null &&
    orderValue !== null;
  // The backend refuses this too; saying so up front saves a round trip.
  const poolLocked = pack !== null && pack.times_sold > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const input = {
        pool,
        label: label.trim(),
        minutes: minutesValue,
        price: priceValue,
        validity_days: validityValue,
        ...(orderValue !== undefined ? { display_order: orderValue } : {}),
      };
      if (pack) {
        await api.recharges.updatePack(pack.pricing_rule_id, input);
        toast.success(`${input.label} updated.`);
      } else {
        await api.recharges.createPack(input);
        toast.success(`${input.label} added to the rate card.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={pack ? `Edit ${pack.label}` : 'New recharge pack'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Select
          label="Pays for"
          value={pool}
          onChange={(event) => setPool(event.target.value as Pool)}
          disabled={poolLocked}
        >
          {POOLS.map((option) => (
            <option key={option} value={option}>
              {POOL_LABEL[option]}
            </option>
          ))}
        </Select>
        {poolLocked && (
          <p className="-mt-2 text-xs text-slate-500">
            Already sold {pack.times_sold} times, so it stays {POOL_LABEL[pack.pool]}. Hide it
            and add a new pack to sell the other kind of time.
          </p>
        )}
        <Input
          label="Name"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="2 Hours"
          hint="What staff see on the sell button."
          required
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Playing time (minutes)"
            value={minutes}
            onChange={(event) => setMinutes(cleanDigits(event.target.value))}
            inputMode="numeric"
            placeholder="120"
            hint={minutesValue ? `= ${formatMinutes(minutesValue)}` : undefined}
            required
          />
          <Input
            label="Price (₹)"
            value={price}
            onChange={(event) => setPrice(cleanPrice(event.target.value))}
            inputMode="decimal"
            placeholder="150"
            hint={
              minutesValue && priceValue !== null
                ? `${formatMoney(perHour({ price: priceValue, minutes: minutesValue }))} per hour`
                : undefined
            }
            required
          />
          <Input
            label="Valid for (days)"
            value={validity}
            onChange={(event) => setValidity(cleanDigits(event.target.value))}
            inputMode="numeric"
            placeholder="3"
            hint="Counted from the moment it is sold."
            required
          />
          <Input
            label="Position"
            value={order}
            onChange={(event) => setOrder(cleanDigits(event.target.value))}
            inputMode="numeric"
            placeholder={pack ? undefined : 'Last'}
            hint="Lower numbers show first."
          />
        </div>

        {/* Stops an admin expecting a customer's existing balance or expiry to
            move when the rate card does. */}
        {pack && (
          <p className="text-xs text-slate-500">
            Changes apply to the next sale. Time already sold keeps the minutes, price and
            expiry it was bought with.
          </p>
        )}

        <Button type="submit" className="w-full" disabled={busy || !valid}>
          {busy ? 'Saving…' : pack ? 'Save changes' : 'Add pack'}
        </Button>
      </form>
    </Modal>
  );
}

function DeletePackDialog({
  pack,
  onClose,
  onDeleted,
}: {
  pack: AdminPricingPack;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.recharges.deletePack(pack.pricing_rule_id);
      toast.success(`${pack.label} removed from the rate card.`);
      onDeleted();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete ${pack.label}?`} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-sm text-slate-300">
          This pack has never been sold, so it can be removed completely. Staff will no longer
          see it when selling time.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="danger" className="flex-1" disabled={busy} onClick={remove}>
            {busy ? 'Deleting…' : 'Delete pack'}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Keep it
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PackTable({
  pool,
  packs,
  busyId,
  onAdd,
  onEdit,
  onToggle,
  onDelete,
}: {
  pool: Pool;
  packs: AdminPricingPack[];
  busyId: string | null;
  onAdd: () => void;
  onEdit: (pack: AdminPricingPack) => void;
  onToggle: (pack: AdminPricingPack) => void;
  onDelete: (pack: AdminPricingPack) => void;
}) {
  // Every row is its own grid, so no column may be `auto`: an auto column sizes
  // to its own row's content and the rows stop lining up with each other.
  const grid =
    'grid grid-cols-[1fr_5rem_6rem] items-center gap-x-4 sm:grid-cols-[1fr_5rem_6rem_5rem_6rem_4rem_11rem]';

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-200">{POOL_LABEL[pool]}</h2>
        <Button variant="secondary" onClick={onAdd}>
          Add {POOL_LABEL[pool]} pack
        </Button>
      </div>
      {packs.length === 0 ? (
        <EmptyState title={`No ${POOL_LABEL[pool]} packs.`}>
          Staff cannot sell this kind of time until a pack is added.
        </EmptyState>
      ) : (
        <Card className="p-0">
          <div
            className={`${grid} border-b border-slate-800 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500`}
          >
            <span>Pack</span>
            <span className="text-right">Time</span>
            <span className="text-right">Price</span>
            <span className="hidden text-right sm:block">Valid for</span>
            <span className="hidden text-right sm:block">Per hour</span>
            <span className="hidden text-right sm:block">Sold</span>
            <span className="hidden sm:block" />
          </div>
          <ul className="divide-y divide-slate-800">
            {packs.map((pack) => (
              <li
                key={pack.pricing_rule_id}
                className={`${grid} px-4 py-3 ${pack.is_active ? '' : 'opacity-60'}`}
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium text-slate-100">
                    <span className="truncate">{pack.label}</span>
                    {!pack.is_active && <Badge tone="amber">Hidden</Badge>}
                  </p>
                  {/* Validity is the one figure staff are asked about at the
                      counter, so it stays visible on a phone too. */}
                  <p className="text-xs text-slate-500 sm:hidden">
                    Valid {formatValidity(pack.validity_days)} · sold {pack.times_sold}×
                  </p>
                </div>
                <p className="text-right text-sm text-slate-300">{formatMinutes(pack.minutes)}</p>
                <p className="text-right text-sm font-semibold text-slate-100">
                  {formatMoney(pack.price)}
                </p>
                <p className="hidden text-right text-sm text-slate-300 sm:block">
                  {formatValidity(pack.validity_days)}
                </p>
                <p className="hidden text-right text-sm text-slate-400 sm:block">
                  {formatMoney(perHour(pack))}
                </p>
                <p className="hidden text-right text-sm text-slate-400 sm:block">
                  {pack.times_sold}
                </p>
                <div className="col-span-3 mt-2 flex justify-end gap-1 sm:col-span-1 sm:mt-0">
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() => onEdit(pack)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    disabled={busyId === pack.pricing_rule_id}
                    onClick={() => onToggle(pack)}
                  >
                    {pack.is_active ? 'Hide' : 'Show'}
                  </Button>
                  {/* A sold pack is on record in customer history, so the
                      backend only lets it be hidden. The button stays, disabled,
                      so every row has the same actions and says why. */}
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                    disabled={pack.times_sold > 0}
                    title={
                      pack.times_sold > 0
                        ? 'Already sold, so it stays on record. Hide it instead.'
                        : undefined
                    }
                    onClick={() => onDelete(pack)}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

export function RechargePacksAdminPage() {
  const toast = useToast();
  const [packs, setPacks] = useState<AdminPricingPack[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<Pool | null>(null);
  const [editing, setEditing] = useState<AdminPricingPack | null>(null);
  const [deleting, setDeleting] = useState<AdminPricingPack | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPacks(await api.recharges.packs());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (pack: AdminPricingPack) => {
    setTogglingId(pack.pricing_rule_id);
    try {
      await api.recharges.updatePack(pack.pricing_rule_id, { is_active: !pack.is_active });
      toast.success(
        pack.is_active
          ? `${pack.label} hidden. Staff will no longer see it when selling.`
          : `${pack.label} is back on the sell screen.`,
      );
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Recharge packs</h1>
        <p className="text-sm text-slate-500">
          The rate card staff sell from. Hidden packs stay on record but do not appear when
          selling.
        </p>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {packs === null && !error ? (
        <Spinner label="Loading the rate card…" />
      ) : (
        packs &&
        POOLS.map((pool) => (
          <PackTable
            key={pool}
            pool={pool}
            packs={packs.filter((pack) => pack.pool === pool)}
            busyId={togglingId}
            onAdd={() => setCreating(pool)}
            onEdit={setEditing}
            onToggle={toggle}
            onDelete={setDeleting}
          />
        ))
      )}

      {creating && (
        <PackFormDialog
          pack={null}
          defaultPool={creating}
          onClose={() => setCreating(null)}
          onSaved={load}
        />
      )}
      {editing && (
        <PackFormDialog
          pack={editing}
          defaultPool={editing.pool}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {deleting && (
        <DeletePackDialog pack={deleting} onClose={() => setDeleting(null)} onDeleted={load} />
      )}
    </div>
  );
}
